/**
 * "References" row of the device Overview: one card per role, detail dialog on click
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatDistanceToNowStrict } from 'date-fns';

import MetricCard from '../common/MetricCard';
import { CggttsCard, MasterRow, References, RoleCard } from '../../types/references';

type CardStatus = 'success' | 'warning' | 'error' | 'info' | 'neutral';

interface CardModel {
  title: string;
  value: string;
  status: CardStatus;
  footer?: string;
  chips?: MasterRow[];
}

const CGGTTS_STATE_LABELS: Record<string, string> = {
  waiting: 'waiting',
  no_reference: 'no L0 reference',
  check_only: 'check only',
  not_configured: 'not configured',
  reference: 'network reference',
};

const ago = (utcSeconds: number | null) =>
  utcSeconds ? `last track accepted ${formatDistanceToNowStrict(new Date(utcSeconds * 1000))} ago` : null;

const mastersCard = (masters: MasterRow[]): CardModel => {
  const counted = masters.filter((m) => !m.excluded);
  const usable = counted.filter((m) => m.usable).length;
  const status: CardStatus = usable === 0 ? 'error' : usable < counted.length ? 'warning' : 'success';
  return { title: 'ADS-B masters', value: `${usable} of ${counted.length} usable`, status, chips: masters };
};

const cggttsCard = (c: CggttsCard): CardModel => {
  const state = c.state ?? '';
  const label = CGGTTS_STATE_LABELS[state];
  // States that replace the followed L0 as the value
  const replacesValue = state === 'no_reference' || state === 'not_configured' || state === 'reference';
  const value = replacesValue ? label : c.master ?? label ?? '—';
  const footer = [replacesValue ? null : label, ago(c.last_accept_utc)].filter(Boolean).join(' · ');
  const status: CardStatus =
    state === 'no_reference' ? 'warning' : state === 'ok' || state === 'reference' ? 'success' : 'neutral';
  return { title: 'CGGTTS master', value, status, footer: footer || undefined };
};

const roleCard = (role: RoleCard, standby: boolean): CardModel => ({
  title: 'ADS-B master role',
  value: role.usable ? 'usable' : 'not usable',
  // An L0 in standby is never a master by design: no alarm colour
  status: role.usable ? 'success' : standby ? 'neutral' : 'warning',
  footer: role.reason ?? undefined,
});

const networkCard = (role: string): CardModel => ({
  title: 'Network role',
  value: role,
  status: role === 'active' ? 'success' : 'neutral',
});

export const buildCards = (r: References): CardModel[] => {
  const cards: CardModel[] = [];
  if (r.network_role) cards.push(networkCard(r.network_role));
  if (r.level === 2 && (r.adsb_masters.length > 0 || r.badge === 'red')) cards.push(mastersCard(r.adsb_masters));
  // The CGGTTS card comes only from the device status: hidden when that is stale
  if (r.cggtts && !r.stale) cards.push(cggttsCard(r.cggtts));
  if (r.role) cards.push(roleCard(r.role, r.network_role === 'standby'));
  return cards;
};

const masterChipLabel = (m: MasterRow) =>
  `${m.key} L${m.level}${m.excluded ? ' · excluded' : m.reason ? ` · ${m.reason}` : ''}`;

const MasterChips: React.FC<{ masters: MasterRow[] }> = ({ masters }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
    {masters.map((m) => (
      <Chip
        key={m.key}
        size="small"
        label={masterChipLabel(m)}
        color={m.excluded ? 'default' : m.usable ? 'success' : 'warning'}
        variant={m.excluded ? 'outlined' : 'filled'}
        sx={m.excluded ? { color: 'text.disabled' } : undefined}
      />
    ))}
  </Box>
);

const yesNo = (v: boolean) => (v ? 'yes' : 'no');

const ReferencesDetail: React.FC<{ references: References; open: boolean; onClose: () => void }> = ({
  references,
  open,
  onClose,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>References</DialogTitle>
    <DialogContent>
      {references.adsb_masters.length > 0 && (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              {['Master', 'Level', 'State', 'Phase', 'Excluded', 'In DTOE'].map((h) => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {references.adsb_masters.map((m) => (
              <TableRow key={m.key}>
                <TableCell>{m.key}</TableCell>
                <TableCell>L{m.level}</TableCell>
                <TableCell>{m.usable ? 'usable' : `not usable${m.reason ? ` (${m.reason})` : ''}`}</TableCell>
                <TableCell>{m.phase_ns === null ? '—' : `${m.phase_ns.toFixed(1)} ns`}</TableCell>
                <TableCell>{yesNo(m.excluded)}</TableCell>
                <TableCell>{yesNo(m.in_dtoe)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {references.cggtts && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          CGGTTS: master {references.cggtts.master ?? '—'}, state {references.cggtts.state ?? '—'}, bias{' '}
          {references.cggtts.bias_ns === null ? '—' : `${references.cggtts.bias_ns.toFixed(1)} ns`}
          {references.cggtts.last_accept_utc ? `, ${ago(references.cggtts.last_accept_utc)}` : ''}
        </Typography>
      )}
      {references.role && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          ADS-B master role: {references.role.usable ? 'usable' : 'not usable'}
          {references.role.reason ? ` (${references.role.reason})` : ''}
          {references.role.last_seen ? `, last message ${references.role.last_seen}` : ''}
        </Typography>
      )}
      {references.config_errors.length > 0 && (
        <Alert severity="warning">Config errors: {references.config_errors.join('; ')}</Alert>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

interface ReferencesRowProps {
  references: References | null;
}

const ReferencesRow: React.FC<ReferencesRowProps> = ({ references }) => {
  const [open, setOpen] = useState(false);

  if (!references) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading references…
      </Typography>
    );
  }

  const cards = buildCards(references);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        References
      </Typography>
      {references.stale && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          References unknown (no status for 60 s)
        </Alert>
      )}
      {references.config_errors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Config errors: {references.config_errors.join('; ')}
        </Alert>
      )}
      <Grid container spacing={3}>
        {cards.map((c) => (
          <Grid item xs={12} md={6} lg={4} key={c.title}>
            <MetricCard title={c.title} value={c.value} status={c.status} footer={c.footer} onClick={() => setOpen(true)}>
              {c.chips && <MasterChips masters={c.chips} />}
            </MetricCard>
          </Grid>
        ))}
      </Grid>
      <ReferencesDetail references={references} open={open} onClose={() => setOpen(false)} />
    </Box>
  );
};

export default ReferencesRow;
